/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server  
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081 
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import {
  getPrompt,
  instructions,
  getInitialPrompt,
} from '../utils/conversation_config.js';
import { WavRenderer } from '../utils/wav_renderer';

import { X, Edit, Zap, ArrowUp, ArrowDown } from 'react-feather';
import { Button } from '../components/button/Button';
import { Toggle } from '../components/toggle/Toggle';
import { Map } from '../components/Map';
import Emily from './Woman_USA.png';
import Noah from './Man_UK.png';
import Jacob from './Man_USA.png';

import './styles.scss';
import { isJsxOpeningLikeElement } from 'typescript';
import { Link } from 'react-router-dom';
import { RealtimeClient } from '../@openai/realtime-api-beta/index.js';
import { ItemType } from '../@openai/realtime-api-beta/dist/lib/client.js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';

const RELAY_BASE: string = (process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '').trim();

/**
 * Builds the full Realtime WebSocket URL.
 * Accepts either:
 * - https://your-host            -> converts to wss://your-host/realtime?model=...
 * - http://your-host             -> converts to ws://your-host/realtime?model=...
 * - wss://your-host/realtime     -> uses as-is, just appends ?model=...
 * - ws://your-host/realtime      -> uses as-is, just appends ?model=...
 */
function buildRelayWsUrl(): string {
  if (!RELAY_BASE) return '';

  // Normalize scheme to ws/wss when http/https is provided
  let base = RELAY_BASE
    .replace(/^http:/i, 'ws:')
    .replace(/^https:/i, 'wss:')
    .replace(/\/+$/, ''); // remove trailing slash

  // Ensure we end with /realtime but do NOT add any query params
  if (!/\/realtime$/i.test(base)) {
    base = `${base}/realtime`;
  }

  return base;
}

/** Transcription while the user speaks Arabic for the translation helper. */
const ARABIC_INPUT_TRANSCRIPTION: { model: string; language?: string } = {
  model: 'gpt-4o-mini-transcribe',
  language: 'ar',
};

/** Default for English practice (push-to-talk / hands-free). */
const ENGLISH_PRACTICE_TRANSCRIPTION: { model: string; language?: string } = {
  model: 'whisper-1',
};

const TRANSLATION_HELPER_RULES = `I will say a word or sentence in Arabic next. This is a translation request, NOT an English grammar check.

Rules for this request only:
- Do NOT say "You should say" or correct my grammar. I am speaking Arabic on purpose.
- Translate the full meaning into natural English, not word-for-word. Treat the whole phrase together.
- I may use Levantine, Egyptian, Gulf, or other spoken Arabic — not only Modern Standard Arabic.
- Many phrases are idioms or fixed expressions: the words do NOT mean what a dictionary says literally. Always check for an idiomatic meaning first in spoken Arabic before giving a literal translation.
- Do not guess from one word inside the phrase (for example, do not assume every phrase with "كيف" means "how are you?", and do not assume every phrase with "العيد" is about the Eid holiday or gifts).
- Give the English equivalent(s) a native English speaker would use for the same idea. If two common options exist, give both briefly.
- If I say "لا قصدي" or "I mean the expression" or correct you, I am telling you it is an idiom — switch to the idiomatic meaning immediately; do not repeat the literal translation.
- If the transcript looks like the wrong language (for example Korean or English garbage) or clearly is not the Arabic I spoke, say briefly that you did not catch it and ask me to say the Arabic phrase again slowly.
- If dialect or context still matters and you are not sure, ask one short clarifying question before translating.
- Keep your English simple and clear for my level, then continue the conversation normally.

Examples of idioms (translate by meaning, NOT literally):
- "على كيفك" → as you like / up to you / fine by me (NOT "how are you?")
- "جبت العيد" / "جاب العيد" → you ruined everything / you messed it up / you spoiled it (NOT "Eid gifts" or "holiday presents")
- "قلبي طاح" → my heart dropped / I was devastated
- "يمشي على البيض" → walks on eggshells / treads carefully
- "دمه ثقيل" → he's a pain / exhausting / tiresome (NOT "heavy drinker")
- "طول بالك" / "طوّل بالك" → take it easy / don't worry / be patient (NOT mainly "watch out")`;

const TRANSLATION_HELPER_OPENING_FIRST =
  'Tell me the word or sentence in Arabic, and I\'ll tell you how to say it in English.';
const TRANSLATION_HELPER_OPENING_REPEAT = 'Tell me the Arabic.';

function buildTranslationHelperPrompt(isFirstClick: boolean): string {
  const opening = isFirstClick
    ? TRANSLATION_HELPER_OPENING_FIRST
    : TRANSLATION_HELPER_OPENING_REPEAT;
  return `${TRANSLATION_HELPER_RULES}

First say exactly: "${opening}"`;
}

interface Message {
  type: 'user' | 'assistant';
  text: string;
}

interface Coordinates {
  lat: number;
  lng: number;
  location?: string;
  temperature?: {
    value: number;
    units: string;
  };
  wind_speed?: {
    value: number;
    units: string;
  };
}

/**
 * Type for all event logs
 */
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

export function ConsolePage() {
  const [isHandsFree, setIsHandsFree] = useState(false);
  const navigate = useNavigate();
  const [Exit, setExit] = useState(false);
  const [searchParams] = useSearchParams();
  const speaker = searchParams.get('speaker');
  const level = searchParams.get('level');
  const [selectedLevel, setSelectedLevel] = useState('');

  const speakerImages: { [key: string]: string } = {
    Emily: Emily,
    Jacob: Jacob,
    Noah: Noah,
  };
  const avatarImage =
    speaker && speakerImages[speaker] ? speakerImages[speaker] : Emily;

  // Modified notify function
  const notify = (message: String) => {
    toast.warn(message, {
      position: 'top-center', // Change position to top-center
      autoClose: false, // Disable auto close
      hideProgressBar: false,
      closeOnClick: true, // Allow close on click
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'light',
    });
  };

  /**
   * Ask user for API Key
   * If we're using the local relay server, we don't need this
   */

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );

  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    // Function to check if the OS is Android
    const isAndroid = () => /Android/i.test(navigator.userAgent);

    // Function to check if the browser is Firefox
    const isFirefox = () => /Firefox/i.test(navigator.userAgent);

    // Function to check if the device is desktop
    const isDesktop = () => !/Mobi|Android/i.test(navigator.userAgent);
    // Set the disabled state if conditions are met
    if ((isAndroid() && isFirefox()) || (isDesktop() && isFirefox())) {
      setIsDisabled(true);
      console.log(
        'Hands-free mode disabled due to browser or OS restrictions.'
      );
    } else {
      console.log('Hands-free mode is enabled.');
    }
  }, []);

  // Removed notify calls from chooseLevel
  const chooseLevel = (level: string) => {
    setSelectedLevel(level);

    if (level) {
      // Default to manual (push-to-talk) to avoid always-on listening costs.
      setIsHandsFree(false);
      changeTurnEndType('none');
      console.log('Defaulting to push-to-talk (hands-free off).');

      // Still start the conversation
      connectConversation(level);
    }
  };


  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      setIsRecording(false);
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      await wavRecorder.pause();
      client.createResponse();
      console.log('Recording stopped.');
    } else {
      // Start recording
      setIsRecording(true);
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
        console.log(
          `Cancelled response for trackId: ${trackId}, offset: ${offset}`
        );
      }
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      console.log('Recording started.');
    }
  };

  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );
  const clientRef = useRef<RealtimeClient>(
    new RealtimeClient({ url: buildRelayWsUrl() })
  );
  // Button prompts sent to the AI but hidden from the user-facing transcript.
  const hiddenTranscriptPromptsRef = useRef(new Set<string>());
  const translationHelperClickCountRef = useRef(0);

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [expandedEvents, setExpandedEvents] = useState<{
    [key: string]: boolean;
  }>({});
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [memoryKv, setMemoryKv] = useState<{ [key: string]: any }>({});
  const [coords, setCoords] = useState<Coordinates | null>({
    lat: 37.775593,
    lng: -122.418137,
  });
  const [simplifiedChat, setSimplifiedChat] = useState<
    { type: 'assistant' | 'user'; text: string }[]
  >([]);
  const [marker, setMarker] = useState<Coordinates | null>(null);

  /**
   * Connect to conversation:
   * WavRecorder takes speech input, WavStreamPlayer output, client is API client
   */
  const connectConversation = useCallback(
    async (level: string) => {
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;
      // wavStreamPlayer.setVolume(1.5);
      // Set state variables
      startTimeRef.current = new Date().toISOString();
      hiddenTranscriptPromptsRef.current.clear();
      translationHelperClickCountRef.current = 0;
      setIsConnected(true);
      setRealtimeEvents([]);
      setItems(client.conversation.getItems());

      // Connect to microphone
      await wavRecorder.begin();
      console.log('Microphone connected.');

      // Connect to audio output
      await wavStreamPlayer.connect();
      wavStreamPlayer.setVolume(1.5);
      console.log({ speaker, level });
      const InitialPrompt = getInitialPrompt(speaker, level);
      console.log('Initial Prompt Sent:', InitialPrompt); // Log the initial prompt
      const { voice, instructions: sessionInstructions } = determineVoiceAndInstructions(
        speaker,
        level
      );
      client.updateSession({ instructions: sessionInstructions });
      client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });
      client.updateSession({
        voice: voice as
          | 'alloy'
          | 'echo'
          | 'verse'
          | 'ash'
          | 'ballad'
          | 'coral'
          | 'sage'
          | 'shimmer',
      });
      client.updateSession({ modalities: ['text', 'audio'], output_audio_format: 'pcm16' });
      // Connect to realtime API
      await client.connect();
      console.log('Realtime API connected.');

      client.sendUserMessageContent([
        {
          type: `input_text`,
          text:
            InitialPrompt ||
            `Say, “Hey, I hope you're doing well today. Would you like to talk about ____, 
                ____, or something else?” In the blanks, choose 2 common conversation topics for an adult with a beginner level of English (A0 level).`,
        },
      ]);
      console.log('Initial prompt sent to assistant.');

      if (client.getTurnDetectionType() === 'server_vad') {
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
        console.log('Server VAD turn detection enabled.');
      }
    },
    [speaker]
  );

  // useEffect(() => {
  //   connectConversation();
  // }, [selectedLevel]);

  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    window.parent.postMessage({ type: "exit" }, "*");
    // window.location.href = "https://speak.englishtodayusa.com/choose_partners"
    // window.location.assign('https://speak.englishtodayusa.com/choose_partners');
    console.log('Redirecting to choose partners page.');

    // Delay state updates until after redirect
    // setTimeout(() => {
    //   setIsConnected(false);
    //   setRealtimeEvents([]);
    //   setItems([]);
    //   setMemoryKv({});
    //   setCoords({
    //     lat: 37.775593,
    //     lng: -122.418137,
    //   });
    //   setMarker(null);

    // const client = clientRef.current;
    // client.disconnect();

    //   const wavRecorder = wavRecorderRef.current;
    //   wavRecorder.end();

    //   const wavStreamPlayer = wavStreamPlayerRef.current;
    //   wavStreamPlayer.interrupt();
    // }, 1000);
  }, []);

  const stopAudioPlayback = async () => {
    const wavStreamPlayer = wavStreamPlayerRef.current;

    if (wavStreamPlayer) {
      await wavStreamPlayer.interrupt();
    }
  };
  // Ensures mic, playback, and Realtime client are fully stopped
  const fullyStopMicAndAudio = async () => {
    try {
      // Stop recording state in UI
      setIsRecording(false);
    } catch (e) {}
  
    // Stop the mic recorder (disconnects microphone)
    try {
      await wavRecorderRef.current?.pause?.();
    } catch (e) {}
    try {
      await wavRecorderRef.current?.end?.();
    } catch (e) {}
  
    // Stop any audio playback
    try {
      await wavStreamPlayerRef.current?.interrupt?.();
    } catch (e) {}
  
    // Disconnect the Realtime client so it stops consuming tokens
    try {
      await clientRef.current?.disconnect?.();
    } catch (e) {}
  };
  const disconnect = async () => {
    await fullyStopMicAndAudio();
    setExit(true);
    console.log('Client disconnected, mic stopped, and exit state set.');
  };

  /**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    if (value === 'none' && wavRecorder.getStatus() === 'recording') {
      await wavRecorder.pause();
      console.log('Recording paused due to turn end type change.');
    }
    client.updateSession({
      turn_detection: value === 'none' ? null : { type: 'server_vad' },
    });
    console.log(
      `Session updated with turn_detection: ${
        value === 'none' ? 'null' : 'server_vad'
      }`
    );
    if (value === 'server_vad' && client.isConnected()) {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      console.log('Recording resumed with server VAD turn detection.');
    }
    setCanPushToTalk(value === 'none');
    console.log(`Can push to talk set to: ${value === 'none'}`);
  };

  /**
   * Auto-scroll the event logs
   */
  useEffect(() => {
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      const scrollHeight = eventsEl.scrollHeight;
      // Only scroll if height has just changed
      if (scrollHeight !== eventsScrollHeightRef.current) {
        eventsEl.scrollTop = scrollHeight;
        eventsScrollHeightRef.current = scrollHeight;
        console.log('Event logs auto-scrolled.');
      }
    }
  }, [realtimeEvents]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
      console.log('Conversation logs auto-scrolled.');
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    const render = () => {
      if (isLoaded) {
        if (clientCanvas) {
          if (!clientCanvas.width || !clientCanvas.height) {
            clientCanvas.width = clientCanvas.offsetWidth;
            clientCanvas.height = clientCanvas.offsetHeight;
          }
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              '#0099ff',
              10,
              0,
              8
            );
          }
        }
        if (serverCanvas) {
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              serverCanvas,
              serverCtx,
              result.values,
              '#009900',
              10,
              0,
              8
            );
          }
        }
        window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
      console.log('Render loop terminated.');
    };
  }, []);

  /**
   * Determine voice and fetch level-specific instructions
   */
  const determineVoiceAndInstructions = (
    speaker: string | null,
    level: string
  ): { voice: string; instructions: string } => {
    let voice = 'sage';
    let customInstructions = 'Speak in a neutral, clear manner.';

    if (speaker === 'Emily') {
      voice = 'sage';
      customInstructions = 'Speak in a calm, conversational way.';
    } else if (speaker === 'Jacob') {
      voice = 'ash';
      customInstructions = 'Speak in a calm, conversational way.';
    } else if (speaker === 'Noah') {
      voice = 'ballad';
      customInstructions =
        'Speak with a British accent in a calm, conversational way.';
    }

    // Fetch level-specific instructions
    const levelInstructions = getPrompt(speaker, level) || customInstructions;

    return {
      voice,
      instructions: levelInstructions,
    };
  };

  useEffect(() => {
    if (!selectedLevel) return;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    const { voice, instructions } = determineVoiceAndInstructions(
      speaker,
      selectedLevel
    );
    console.log({ instructions, voice });

    client.updateSession({ instructions: instructions });

    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    client.updateSession({
      voice: voice as
        | 'alloy'
        | 'echo'
        | 'verse'
        | 'ash'
        | 'ballad'
        | 'coral'
        | 'sage'
        | 'shimmer',
    });

    client.addTool(
      {
        name: 'set_memory',
        description: 'Saves important data about the user into memory.',
        parameters: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description:
                'The key of the memory value. Always use lowercase and underscores, no other characters.',
            },
            value: {
              type: 'string',
              description: 'Value can be anything represented as a string',
            },
          },
          required: ['key', 'value'],
        },
      },
      async ({ key, value }: { [key: string]: any }) => {
        setMemoryKv((memoryKv) => {
          const newKv = { ...memoryKv };
          newKv[key] = value;
          return newKv;
        });
        return { ok: true };
      }
    );
    

    // Add tools (if any, currently commented out)

    // handle realtime events from client + server for event logging
    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      console.log('Realtime Event Received:', realtimeEvent);
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });

    client.on('error', (event: any) => {
      console.error('Realtime Client Error:', event);
    });

    client.on('conversation.interrupted', async () => {
      console.log('Conversation interrupted.');
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
        console.log(
          `Cancelled response for trackId: ${trackId}, offset: ${offset}`
        );
      }
    });

    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000
        );
        item.formatted.file = wavFile;
      }
      setItems(items);

      // Log the assistant's response
      console.log(
        'Assistant Response:',
        item.formatted.text ||
          item.formatted.transcript ||
          '(No text available)'
      );
    });

    setItems(client.conversation.getItems());
    console.log('Conversation items set.');

    return () => {
      // cleanup; resets to defaults
      client.reset();
      console.log('Client reset on cleanup.');
    };
  }, [selectedLevel, speaker]);

  useEffect(() => {
    const newChatLog = items
      .filter((conversationItem) => {
        if (
          conversationItem.role !== 'assistant' &&
          conversationItem.role !== 'user'
        ) {
          return false;
        }
        if (conversationItem.role === 'user') {
          const text = (
            conversationItem.formatted.text ||
            conversationItem.formatted.transcript ||
            ''
          ).trim();
          if (hiddenTranscriptPromptsRef.current.has(text)) {
            return false;
          }
        }
        return true;
      })
      .map((conversationItem) => ({
        type: conversationItem.role as 'assistant' | 'user',
        text:
          conversationItem.formatted.text ||
          conversationItem.formatted.transcript ||
          '(No text available)',
      }));

    setSimplifiedChat(newChatLog);
    localStorage.setItem('simplifiedChat', JSON.stringify(newChatLog));

    // Log the updated chat log
    console.log('Updated Chat Log:', newChatLog);
  }, [items]);

  const resetEnglishPracticeTranscription = () => {
    clientRef.current.updateSession({
      input_audio_transcription: { ...ENGLISH_PRACTICE_TRANSCRIPTION },
    });
  };

  const sendPredefinedMessage = async (message: string) => {
    if (!isConnected) {
      console.error('Not connected to the server.');
      return;
    }
    try {
      resetEnglishPracticeTranscription();
      hiddenTranscriptPromptsRef.current.add(message.trim());
      const client = clientRef.current;
      client.sendUserMessageContent([
        {
          type: `input_text`,
          text: message,
        },
      ]);
      console.log(`Predefined message sent: "${message}"`);
    } catch (error) {
      console.error('Error sending predefined message:', error);
    }
  };

  const sendTranslationHelper = async () => {
    if (!isConnected) {
      console.error('Not connected to the server.');
      return;
    }
    try {
      const client = clientRef.current;
      const isFirstClick = translationHelperClickCountRef.current === 0;
      const message = buildTranslationHelperPrompt(isFirstClick);
      translationHelperClickCountRef.current += 1;

      client.updateSession({
        input_audio_transcription: { ...ARABIC_INPUT_TRANSCRIPTION },
      });
      hiddenTranscriptPromptsRef.current.add(message.trim());
      client.sendUserMessageContent([
        {
          type: `input_text`,
          text: message,
        },
      ]);
      console.log(
        `Translation helper sent (first=${isFirstClick}, transcription=ar)`,
      );
    } catch (error) {
      console.error('Error sending translation helper:', error);
    }
  };

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage">
      <ToastContainer />
      <div
        id="levelPage"
        style={{ display: selectedLevel === '' ? 'block' : 'none' }}
        className="container"
      >
        <h1>Choose your English level</h1>
        <p>اختر مستواك في اللغة الإنجليزية</p>
        <div className="buttons">
          {[
            { english: 'Beginner', arabic: 'مبتدئ' },
            { english: 'Elementary', arabic: 'المستوى الأساسي' },
            { english: 'Pre-Intermediate', arabic: 'المستوى قبل المتوسط' },
            { english: 'Intermediate', arabic: 'المستوى المتوسط' },
            { english: 'Upper Intermediate', arabic: 'فوق المتوسط' },
            { english: 'Advanced', arabic: 'متقدم' },
          ].map((level, index) => (
            <button
              key={index}
              className={`button level-button ${
                selectedLevel === level.english ? 'selected' : ''
              }`}
              onClick={() => chooseLevel(level.english)}
            >
              <span className="english">{level.english}</span>
              <span className="arabic">{level.arabic}</span>
            </button>
          ))}
        </div>
      </div>
      <div
        className="main"
        style={{
          display: selectedLevel !== '' && !Exit ? 'block' : 'none',
        }}
      >
        {/* <div className="main" style={{ display: 'block' }}> */}
        <div id="chatPage" className={`container`}>
          {/* hide the button of connect and disconnect 5 dec*/}
          {/* <button
            className={`my-button ${isConnected ? 'disconnect' : 'connect'}     `}
            onClick={isConnected ? disconnectConversation : connectConversation}
          >
            {isConnected ? 'Disconnect' : 'Connect'}
          </button> */}

          <h1 style={{ marginBottom: '10px', fontSize: '24px' }}>
            Practice speaking English with me. You can do it!
          </h1>
          <p
            style={{ marginTop: '5px', direction: 'rtl', textAlign: 'center' }}
          >
            تدرّب على التحدث بالإنجليزية معي. أنت أهل لها!
          </p>

          <img
            src={avatarImage}
            alt={speaker || 'Speaker Avatar'}
            className="avatar"
            id="avatarImage"
          />

          <div className="visualization sound">
            <div
              className="visualization-entry client"
              style={{ height: '50px' }}
            >
              <canvas
                ref={clientCanvasRef}
                style={{ width: '100%', height: '50px' }}
              />
            </div>
            <div
              className="visualization-entry server"
              style={{ height: '50px' }}
            >
              <canvas
                ref={serverCanvasRef}
                style={{ width: '100%', height: '50px' }}
              />
            </div>
          </div>
          <div className="buttons">
            <div className="toggle-container">
              <button
                onClick={() => {
                  setIsHandsFree(false);
                  changeTurnEndType('none');
                  console.log('Switched to manual speaking mode.');
                }}
                className={`toggle-button ${
                  !isHandsFree ? 'active' : 'inactive'
                } `}
              >
                <span className="icon">
                  {!isHandsFree ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="7"
                        fill="transparent"
                        stroke="black"
                        strokeWidth="2"
                      ></circle>
                      <circle
                        cx="8"
                        cy="8"
                        r="5"
                        fill="black"
                        className="border_"
                      ></circle>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  )}
                </span>
                <div className="button-content">
                  <span className="primary-text">Click to speak</span>
                  <span className="secondary-text">انقر للتحدث</span>
                </div>
              </button>

              {/* Modified Hands-free Toggle Button */}
              <button
                onClick={() => {
                  if (isDisabled) {
                    notify(
                      'Hands-free mode is not available on Firefox. Please use another browser such as Google Chrome or Safari. وضع الاستخدام بدون استخدام اليد غير متوفر على متصفح "فايرفوكس". يُرجى استخدام متصفح آخر مثل جوجل كروم أو سفاري'
                    );
                    console.log(
                      'Attempted to enable hands-free mode on disabled browser.'
                    );
                  } else {
                    setIsHandsFree(true);
                    changeTurnEndType('server_vad');
                    console.log('Hands-free mode enabled.');
                  }
                }}
                // Removed disabled attribute
                className={`toggle-button ${
                  isHandsFree ? 'active' : 'inactive'
                } ${isDisabled ? 'disabled' : ''}`}
              >
                <span className="icon">
                  {isHandsFree ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="7"
                        fill="transparent"
                        stroke="black"
                        strokeWidth="2"
                      ></circle>
                      <circle
                        cx="8"
                        cy="8"
                        r="5"
                        fill="black"
                        className="border_"
                      ></circle>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  )}
                </span>
                <div className="button-content">
                  <span className="primary-text">Hands-free</span>
                  <span className="secondary-text">
                    {isDisabled ? 'Disabled on Firefox' : 'بدون استخدام اليد'}
                  </span>
                </div>
              </button>
            </div>

            <button
              className={`button ask wide-button ${
                isRecording ? 'recording' : 'regular'
              }`}
              disabled={!isConnected || !canPushToTalk}
              onClick={toggleRecording} // Toggle on click
            >
              <div>
                <span className="english">
                  {isHandsFree
                    ? isRecording
                      ? "Speak when you're ready🗣️"
                      : "Speak when you're ready🗣️"
                    : isRecording
                    ? '👉 Click to stop recording 👈'
                    : '👉 Click here to speak 👈'}
                </span>

                <span className="arabic">
                  {isHandsFree
                    ? isRecording
                      ? 'تحدث عندما تكون مستعدًا'
                      : 'تحدث عندما تكون مستعدًا'
                    : isRecording
                    ? 'انقر لإيقاف التسجيل'
                    : 'انقر هنا للتحدث'}
                </span>
              </div>
            </button>

            <div className="buttons-grid">
              <button
                className="helper-button"
                onClick={() =>
                  sendPredefinedMessage(
                    "I didn't understand what you said. Can you say it again in an easier way?"
                  )
                }
              >
                <span className="english">I don't understand</span>
                <span className="arabic">لا أفهم</span>
              </button>
              <button
                className="helper-button"
                onClick={() =>
                  sendPredefinedMessage(
                    'Did I make any other mistakes in what I said? If so, correct every single mistake I made in order \(first, second, third, etc.\), from the first mistake I made to the last mistake I made. For each mistake I made, first say the mistake I said and then next say what I should say instead. Speak slowly. Then continue the conversation and return to the normal instructions.'
                  )
                }
              >
                <span className="english">Correct all my mistakes</span>
                <span className="arabic">صحح كل أخطائي</span>
              </button>
              <button
                className="helper-button"
                onClick={() =>
                  sendPredefinedMessage(
                    'Explain my mistake to me in very simple English. Tell me the rule and explain it like you\'re a TESOL teacher helping an A2-level student learn from their \
mistakes so I can understand the rule and avoid repeating the mistake in the future. Then continue the conversation.'
                  )
                }
              >
                <span className="english">Explain my mistake</span>
                <span className="arabic">اشرح خطائي</span>
              </button>
              <button
                className="helper-button"
                onClick={() =>
                  sendPredefinedMessage(
                    'Please ask me a different question instead of the one you asked me.'
                  )
                }
              >
                <span className="english">Ask me a different question</span>
                <span className="arabic">اسألني سؤالاً مختلفاً</span>
              </button>

              <button
                className="helper-button"
                onClick={() => sendTranslationHelper()}
              >
                <span className="english">How do I say __ in English?</span>
                <span className="arabic">كيف أقول _ بالإنجليزية</span>
              </button>
              <button
                className="helper-button"
                id="endConversationButton"
                onClick={disconnect}
              >
                <span className="english">
                  🛑End the🛑
                  <br />
                  conversation
                </span>
                <span className="arabic">إنهاء المحادثة </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="main" style={{ display: Exit ? 'block' : 'none' }}>
        {/* <div className="main" style={{ display: 'none' }}> */}
        <div
          style={{
            margin: 0,
            fontFamily: 'Arial, sans-serif',
            background:
              'linear-gradient(to bottom, #ffffff, #d0faff, #3fc9b9, #03989e)',
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            className="transcript-card"
            style={{
              textAlign: 'center',
              padding: '20px',
              width: '100%',
              maxWidth: '800px',
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '10px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                color: '#0056b3',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
              }}
            >
              Transcript
            </h1>
            <h1
              style={{
                fontSize: '24px',
                color: '#0056b3',
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
              }}
            >
              نص المحادثة
            </h1>
            {simplifiedChat.length === 0 ? (
              <p
                style={{
                  color: '#555',
                  fontStyle: 'italic',
                  marginTop: '20px',
                }}
              >
                No conversation history available.
              </p>
            ) : (
              <ul
                style={{ listStyleType: 'none', padding: 0, margin: '20px 0' }}
              >
                {simplifiedChat
                  .slice(1) // Skip the first item if needed
                  .filter(
                    (message) =>
                      message.text &&
                      message.text.trim() !== '' &&
                      message.text.trim() !== '(No text available)'
                  )
                  .reduce<Message[]>((acc, message) => {
                    // Check if it's a user message and if the last message was from the same user
                    if (message.type === 'user') {
                      if (
                        acc.length > 0 &&
                        acc[acc.length - 1].type === 'user'
                      ) {
                        // Append user message to the last one if it's consecutive
                        acc[acc.length - 1].text += ' ' + message.text;
                      } else {
                        // Otherwise, add the new user message as a new entry
                        acc.push({ ...message });
                      }
                    } else {
                      // For assistant messages, just push them directly
                      acc.push({ ...message });
                    }
                    return acc;
                  }, [])
                  .map((message, index) => (
                    <li
                      key={index}
                      style={{
                        background: '#ffffff',
                        border: '1px solid #007bff',
                        marginBottom: '10px',
                        padding: '10px',
                        borderRadius: '5px',
                        color: '#333',
                      }}
                    >
                      <strong style={{ color: '#007bff' }}>
                        {message.type === 'user' ? 'You: ' : 'SpeakSmart: '}
                      </strong>
                      {message.text}
                    </li>
                  ))}
              </ul>
            )}
            <button
              onClick={() => disconnectConversation()}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                color: '#ffffff',
                backgroundColor: '#007bff',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                marginTop: '20px',
              }}
            >
              Exit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
